import { Placement } from '@floating-ui/react'
import { QueryKey } from '@tanstack/react-query'
import { isModelVirtualWeatherStation } from '@weenat/client/dist/core/devices'
import isIrrigationDevice from '@weenat/client/dist/core/devices/isIrrigationDevice'
import { getDifferenceInMinutes } from '@weenat/client/dist/core/history'
import {
  PureDataMarkerProps,
  getMarkerBackgroundColor,
  getMarkerColorScale
} from '@weenat/client/dist/core/map'
import {
  PointProperties,
  extractValueFromSummaryData,
  getZIndex
} from '@weenat/client/dist/core/measurements'
import { getMetricProviderInfoFromSummary } from '@weenat/client/dist/core/metrics'
import {
  computeHasNotEmitted,
  getMetricLastUpdateFromSummary
} from '@weenat/client/dist/core/summary'
import {
  Universe,
  getAvailableUniversesFromMetrics,
  getMetricsFromSummary,
  isFreemiumAccessibleAggregate
} from '@weenat/client/dist/core/universes'
import { Aggregations } from '@weenat/client/dist/enums'
import { getLastUpdateThresholds } from '@weenat/client/dist/enums/Devices'
import { isFrost, isSoilSensorMetric } from '@weenat/client/dist/enums/MetricIds'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import {
  isDeviceSummary,
  isPlotSummary,
  isStationSummary
} from '@weenat/client/dist/resources/measurements.type'
import { isArvalisPlotSummary } from '@weenat/client/dist/resources/waterBalanceArvalis'
import { Href } from '@weenat/client/dist/routx/runtime-core'
import { WeenatColor } from '@weenat/theme'
import { useNavigate } from 'app/routx-router'
import { useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import WindDirectionIcon from 'app/src/kit/icons/WindDirectionIcon'
import { BoxProps } from 'app/src/kit/primitives/themeMappings/props'
import { Tooltip, TooltipContent, TooltipTrigger } from 'app/src/kit/tooltips/Tooltip'
import { useToggleFeature, useUniverse, useUniverseHorizon } from 'app/state'
import { DebouncedFunc } from 'lodash-es'
import isNil from 'lodash-es/isNil'
import { readableColor } from 'polished'
import { memo, useCallback, useMemo } from 'react'
import { styled, useTheme } from 'styled-components'
import SummaryCard from '../SummaryCard'
import {
  useBackgroundMapContext,
  useBackgroundMapDispatcher
} from '../contexts/BackgroundMapContext'
import { TOOLTIP_CLOSE_DELAY_MS } from '../hooks/usePODClusters'
import useUniverseBackgroundMapContext from '../universes/useUniverseBackgroundMapContext'
import DiscreteDataMarker from './DiscreteDataMarker'
import MarkerText from './MarkerText'
import PureDataMarker from './PureDataMarker'
import RestrictedDataMarker from './RestrictedDataMarker'

const TOOLTIP_STYLE = {
  padding: 0,
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 0,
  boxShadow: 'none'
}

const TOOLTIP_REST_DELAY = 100

const PLACEMENT: Placement = 'top'
const offsetFromTrigger = 64

const DataMarkerTooltip: FC = ({ children }) => {
  return (
    <Tooltip
      enableSafePolygon
      restMs={TOOLTIP_REST_DELAY}
      closeDelay={TOOLTIP_CLOSE_DELAY_MS}
      placement={PLACEMENT}
      offsetFromTrigger={offsetFromTrigger}
      useOffsetOnFlip={false}
    >
      {children}
    </Tooltip>
  )
}

/**
 * Outer wrapper that repositions the bottom-center of the marker stack to the lat/lng coordinate.
 * Google Maps places a child's top-left corner at the coordinate by default.
 *
 * .attrs() writes --marker-z-index directly into the element's inline style.
 * This avoids styled-components generating a new CSS class for every unique zIndex value
 * (which happens on every sensor data update / hover event across all map markers).
 * The CSS template stays static; only the custom property value changes at runtime.
 */
const MarkerPositioner = styled.div.attrs<{ $zIndex: number }>(({ $zIndex }) => ({
  style: { '--marker-z-index': $zIndex } as React.CSSProperties
}))`
  position: relative;
  width: fit-content;
  transform: translate(-50%, -100%);
  z-index: var(--marker-z-index);
  cursor: pointer;
  pointer-events: auto;
`

interface Props
  extends Pick<
    PointProperties,
    'isFavorite' | 'countByMetric' | 'summary' | 'isVirtualDeviceByMetric'
  > {
  lat: number
  lng: number
  resourceId: number | string | undefined
  summaryLink: Href
  debouncedReset: DebouncedFunc<() => void>
  summariesQueryCacheKey?: QueryKey
  type: PureDataMarkerProps['type']
  timezone: string | undefined
}

/**
 * DataMarker – the redesigned map marker for plots, stations and devices.
 * Uses PureDataMarker for visuals and keeps the web hover/tooltip interaction model.
 */
const DataMarker: FC<Props> = ({
  resourceId,
  isFavorite,
  countByMetric,
  summaryLink,
  isVirtualDeviceByMetric,
  type,
  lat: _lat,
  lng: _lng,
  summary,
  debouncedReset,
  summariesQueryCacheKey,
  timezone
}) => {
  const dispatch = useBackgroundMapDispatcher()
  const nav = useNavigate()
  const { colors } = useTheme()
  const { convertValue } = useConvertedValue()
  const [{ smv }] = useToggleFeature()

  const [currentUniv] = useUniverse()
  const [horizon] = useUniverseHorizon()

  const {
    focusedMetricId,
    aggregate: aggregateFromContext,
    universeAggregate,
    span
  } = useUniverseBackgroundMapContext()

  const aggregate =
    !isNil(focusedMetricId) && isFrost(focusedMetricId)
      ? Aggregations.lastValues
      : aggregateFromContext

  const { hoverIntent } = useBackgroundMapContext()

  const isFocused = hoverIntent.itemId === resourceId

  const boxShadow: BoxProps['$boxShadow'] | undefined = isFocused ? 'lg' : undefined
  const borderColor: WeenatColor | undefined = isFocused ? 'primary.500' : undefined

  const isVirtualDevice = smv
    ? isDeviceSummary(summary)
      ? isModelVirtualWeatherStation(summary.metadata.device.model)
      : isStationSummary(summary)
        ? isModelVirtualWeatherStation(summary.metadata.station.model)
        : false
    : !isNil(focusedMetricId) && (isVirtualDeviceByMetric.get(focusedMetricId) ?? false)

  const isVirtualWaterTank =
    focusedMetricId === 'WATER_TANK_LEVEL' &&
    (isVirtualDeviceByMetric.get(focusedMetricId) ?? false)

  const { isRestricted: isAccessRestrictedByPlan } = useIsAppAccessRestricted({
    isVWS: isVirtualDevice
  })

  const dataFromSummary =
    !isNil(focusedMetricId) && !isNil(universeAggregate)
      ? extractValueFromSummaryData({
          data: summary.data,
          metricId: focusedMetricId,
          aggregate,
          universeAggregate,
          horizon
        })
      : undefined

  const isDiscrete =
    currentUniv === Universe.overview ||
    (!isNil(focusedMetricId) &&
      isSoilSensorMetric(focusedMetricId) &&
      ((isDeviceSummary(summary) && isIrrigationDevice(summary.metadata.device)) ||
        (isStationSummary(summary) && isIrrigationDevice(summary.metadata.station)))) ||
    (!isNil(focusedMetricId) && !countByMetric.has(focusedMetricId))

  const hasNotEmitted = !isNil(focusedMetricId)
    ? computeHasNotEmitted(summary, focusedMetricId, isVirtualDevice || isVirtualWaterTank)
    : false

  const lastUpdate = !isNil(focusedMetricId)
    ? getMetricLastUpdateFromSummary({
        summary,
        metric: focusedMetricId,
        horizonId: horizon
      })
    : undefined

  const isArvalisPlot = isArvalisPlotSummary(summary)
  const shouldConfigureArvalis = isArvalisPlot && !summary.plotIsSetupForCurrentSeason
  const shouldConfigureMetric =
    !isNil(focusedMetricId) && isPlotSummary(summary)
      ? (summary.metadata.notConfigured?.includes(focusedMetricId) ?? false)
      : false

  const needsConfig = shouldConfigureArvalis || shouldConfigureMetric

  const zIndex = useMemo(
    () =>
      getZIndex({
        isFavorite,
        isFocused,
        isDiscrete,
        needsConfig,
        hasNotEmitted,
        metricId: focusedMetricId,
        rawValue: dataFromSummary?.value
      }),
    [
      isFavorite,
      isFocused,
      isDiscrete,
      needsConfig,
      hasNotEmitted,
      focusedMetricId,
      dataFromSummary?.value
    ]
  )

  const availableUniverses = useMemo(() => {
    const measures = getMetricsFromSummary(summary)
    return getAvailableUniversesFromMetrics(measures)
  }, [summary])

  // Computed unconditionally so the JSX `content` node is stable across renders.
  // By the time the final return is reached, hasNotEmitted is always false
  // (the earlier early-return handles that case), so we can safely hardcode it.
  const withDataContent = useMemo(() => {
    if (isNil(focusedMetricId)) return null

    const { value = null, threshold = null, trend = null } = dataFromSummary ?? {}

    const colorScale = getMarkerColorScale(focusedMetricId, span)

    const backgroundColor = getMarkerBackgroundColor({
      rawValue: value,
      colorScale,
      colors,
      focusedMetricId,
      threshold,
      isRestricted: false,
      hasNotEmitted: false
    })

    const convertedAndFormattedValue = convertValue({
      metric: focusedMetricId,
      value,
      displayUnit: !isNil(value),
      showEmptyState: true
    })

    const contentColor = readableColor(backgroundColor)

    const content =
      focusedMetricId === 'DD' && !isNil(value) && Number.isFinite(value) ? (
        <WindDirectionIcon windOrigin={value} $color={contentColor as WeenatColor} $size='lg' />
      ) : (
        <MarkerText $color={contentColor as WeenatColor}>{convertedAndFormattedValue}</MarkerText>
      )

    const matchingConfig = isPlotSummary(summary)
      ? summary.metadata.plot.currentMeasurementConfigs.find((e) => e.metric === focusedMetricId)
      : undefined

    const { brand, model } = getMetricProviderInfoFromSummary({
      resource: isPlotSummary(summary)
        ? summary.metadata.plot
        : isDeviceSummary(summary)
          ? summary.metadata.device
          : summary.metadata.station,
      measurementConfig: matchingConfig
    })

    const updateThresholds = getLastUpdateThresholds(
      brand,
      focusedMetricId,
      isModelVirtualWeatherStation(model)
    )

    return { value, threshold, trend, backgroundColor, contentColor, content, updateThresholds }
  }, [dataFromSummary, focusedMetricId, span, colors, convertValue, summary])

  const setHoverIntent = useCallback(() => {
    debouncedReset.cancel()
    dispatch({
      type: 'setHoverIntent',
      newHoverIntent: {
        itemId: resourceId,
        source: 'marker'
      }
    })
  }, [dispatch, resourceId, debouncedReset])

  const cancelDebouncedReset = useCallback(() => {
    debouncedReset.cancel()
  }, [debouncedReset])

  const invokeDebouncedResetImmediately = useCallback(() => {
    debouncedReset()
    debouncedReset.flush()
  }, [debouncedReset])

  const pressAndClearIntent = useCallback(() => {
    invokeDebouncedResetImmediately()
    nav(summaryLink)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryLink, invokeDebouncedResetImmediately])

  const isRestricted = isAccessRestrictedByPlan
    ? isVirtualDeviceByMetric.get(focusedMetricId)
      ? !isFreemiumAccessibleAggregate(universeAggregate)
      : true
    : false

  // Restricted state
  if (isRestricted && !isNil(resourceId)) {
    return (
      <DataMarkerTooltip>
        <TooltipTrigger onMouseEnter={setHoverIntent} onMouseLeave={debouncedReset}>
          <MarkerPositioner $zIndex={zIndex} onClick={pressAndClearIntent}>
            <RestrictedDataMarker type={type} borderColor={borderColor} boxShadow={boxShadow} />
          </MarkerPositioner>
        </TooltipTrigger>
        <TooltipContent
          style={TOOLTIP_STYLE}
          onMouseEnter={cancelDebouncedReset}
          onMouseLeave={debouncedReset}
        >
          {isFocused && availableUniverses.length === 1 && (
            <SummaryCard
              summary={summary}
              focusedHorizon={null}
              focusedMetric={null}
              onPress={pressAndClearIntent}
              summariesQueryCacheKey={summariesQueryCacheKey}
            />
          )}
        </TooltipContent>
      </DataMarkerTooltip>
    )
  }

  // Always discrete in the Overview universe
  if (currentUniv === Universe.overview) {
    return (
      <DataMarkerTooltip>
        <TooltipTrigger onMouseEnter={setHoverIntent} onMouseLeave={debouncedReset}>
          <MarkerPositioner $zIndex={zIndex} onClick={pressAndClearIntent}>
            <DiscreteDataMarker type={type} borderColor={borderColor} boxShadow={boxShadow} />
          </MarkerPositioner>
        </TooltipTrigger>
        <TooltipContent
          style={TOOLTIP_STYLE}
          onMouseEnter={cancelDebouncedReset}
          onMouseLeave={debouncedReset}
        >
          {isFocused && availableUniverses.length === 1 && (
            <SummaryCard
              summary={summary}
              focusedHorizon={null}
              focusedMetric={null}
              onPress={pressAndClearIntent}
              summariesQueryCacheKey={summariesQueryCacheKey}
            />
          )}
        </TooltipContent>
      </DataMarkerTooltip>
    )
  }

  if (isNil(focusedMetricId)) {
    return null
  }

  // Discrete for irrigation devices with soil-sensor metric
  if (
    isSoilSensorMetric(focusedMetricId) &&
    ((isDeviceSummary(summary) && isIrrigationDevice(summary.metadata.device)) ||
      (isStationSummary(summary) && isIrrigationDevice(summary.metadata.station)))
  ) {
    return (
      <DataMarkerTooltip>
        <TooltipTrigger onMouseEnter={setHoverIntent} onMouseLeave={debouncedReset}>
          <MarkerPositioner $zIndex={zIndex} onClick={pressAndClearIntent}>
            <DiscreteDataMarker type={type} borderColor={borderColor} boxShadow={boxShadow} />
          </MarkerPositioner>
        </TooltipTrigger>
        <TooltipContent
          style={TOOLTIP_STYLE}
          onMouseEnter={cancelDebouncedReset}
          onMouseLeave={debouncedReset}
        >
          {isFocused && (
            <SummaryCard
              summary={summary}
              focusedHorizon={horizon}
              focusedMetric={focusedMetricId}
              onPress={pressAndClearIntent}
              summariesQueryCacheKey={summariesQueryCacheKey}
            />
          )}
        </TooltipContent>
      </DataMarkerTooltip>
    )
  }

  // Discrete when metric not in countByMetric
  if (!countByMetric.has(focusedMetricId)) {
    return (
      <DataMarkerTooltip>
        <TooltipTrigger onMouseEnter={setHoverIntent} onMouseLeave={debouncedReset}>
          <MarkerPositioner $zIndex={zIndex} onClick={pressAndClearIntent}>
            <DiscreteDataMarker type={type} borderColor={borderColor} boxShadow={boxShadow} />
          </MarkerPositioner>
        </TooltipTrigger>
        <TooltipContent
          style={TOOLTIP_STYLE}
          onMouseEnter={cancelDebouncedReset}
          onMouseLeave={debouncedReset}
        >
          {isFocused && (
            <SummaryCard
              summary={summary}
              focusedHorizon={null}
              focusedMetric={focusedMetricId}
              isDiscrete
              onPress={pressAndClearIntent}
              summariesQueryCacheKey={summariesQueryCacheKey}
            />
          )}
        </TooltipContent>
      </DataMarkerTooltip>
    )
  }

  // No emission state
  if (hasNotEmitted) {
    return (
      <DataMarkerTooltip>
        <TooltipTrigger onMouseEnter={setHoverIntent} onMouseLeave={debouncedReset}>
          <MarkerPositioner $zIndex={zIndex} onClick={pressAndClearIntent}>
            <PureDataMarker
              color={'black'}
              backgroundColor={'grayscale.100'}
              lastUpdate={undefined}
              content={null}
              trend={undefined}
              showLastUpdate={false}
              showStatusIndicator
              status={'not_emitted'}
              type={type}
              showContent={false}
              boxShadow={boxShadow}
              isFocused={isFocused}
              timezone={timezone}
            />
          </MarkerPositioner>
        </TooltipTrigger>
        <TooltipContent
          style={TOOLTIP_STYLE}
          onMouseEnter={cancelDebouncedReset}
          onMouseLeave={debouncedReset}
        >
          {isFocused && (
            <SummaryCard
              summary={summary}
              focusedHorizon={horizon}
              focusedMetric={focusedMetricId}
              onPress={pressAndClearIntent}
              summariesQueryCacheKey={summariesQueryCacheKey}
            />
          )}
        </TooltipContent>
      </DataMarkerTooltip>
    )
  }

  if (shouldConfigureArvalis || shouldConfigureMetric) {
    return (
      <DataMarkerTooltip>
        <TooltipTrigger onMouseEnter={setHoverIntent} onMouseLeave={debouncedReset}>
          <MarkerPositioner $zIndex={zIndex} onClick={pressAndClearIntent}>
            <PureDataMarker
              color={'black'}
              backgroundColor={'grayscale.100'}
              lastUpdate={undefined}
              content={null}
              trend={undefined}
              showLastUpdate={false}
              showStatusIndicator
              status={'config'}
              type={type}
              showContent={false}
              boxShadow={boxShadow}
              isFocused={isFocused}
              timezone={timezone}
            />
          </MarkerPositioner>
        </TooltipTrigger>
        <TooltipContent
          style={TOOLTIP_STYLE}
          onMouseEnter={cancelDebouncedReset}
          onMouseLeave={debouncedReset}
        >
          {isFocused && (
            <SummaryCard
              summary={summary}
              focusedHorizon={horizon}
              focusedMetric={focusedMetricId}
              onPress={pressAndClearIntent}
              summariesQueryCacheKey={summariesQueryCacheKey}
            />
          )}
        </TooltipContent>
      </DataMarkerTooltip>
    )
  }

  if (isNil(withDataContent)) return null

  const { trend, backgroundColor, contentColor, content, updateThresholds } = withDataContent

  const timeSinceLastMessageInMinutes = !isNil(lastUpdate)
    ? getDifferenceInMinutes(lastUpdate)
    : null

  const warningThresholdReached =
    !isNil(lastUpdate) &&
    !isNil(timeSinceLastMessageInMinutes) &&
    timeSinceLastMessageInMinutes > updateThresholds.warningThreshold

  return (
    <DataMarkerTooltip>
      <TooltipTrigger onMouseEnter={setHoverIntent} onMouseLeave={debouncedReset}>
        <MarkerPositioner $zIndex={zIndex} onClick={pressAndClearIntent}>
          <PureDataMarker
            backgroundColor={backgroundColor}
            color={contentColor}
            showLastUpdate={warningThresholdReached}
            lastUpdate={lastUpdate}
            content={content}
            trend={trend}
            showStatusIndicator={false}
            status={undefined}
            type={type}
            warningThreshold={updateThresholds.warningThreshold}
            dangerThreshold={updateThresholds.dangerThreshold}
            isFocused={isFocused}
            boxShadow={boxShadow}
            timezone={timezone}
          />
        </MarkerPositioner>
      </TooltipTrigger>
      <TooltipContent
        style={TOOLTIP_STYLE}
        onMouseEnter={cancelDebouncedReset}
        onMouseLeave={debouncedReset}
      >
        {isFocused && (
          <SummaryCard
            summary={summary}
            focusedHorizon={horizon}
            focusedMetric={focusedMetricId}
            onPress={pressAndClearIntent}
            summariesQueryCacheKey={summariesQueryCacheKey}
            isVirtual={isVirtualDevice || isVirtualWaterTank}
          />
        )}
      </TooltipContent>
    </DataMarkerTooltip>
  )
}

export default memo(DataMarker)
